/**
 * CTRL_02_ConfigPanel - Model Risk Classification Configuration
 */
import React, { useState, useEffect } from 'react';
import { Card, Switch, Button, Collapse, Select, Tag, Alert, Spin, Checkbox } from 'antd';
import {
  SettingOutlined,
  CheckCircleOutlined,
  WarningOutlined,
  SaveOutlined
} from '@ant-design/icons';
import { getControlConfig, saveControlConfig } from '../api/trustgridApi';
import { showError, showSuccess } from '../../../utils/notifications';

const { Panel } = Collapse;
const { Option } = Select;

// EU AI Act risk levels
const RISK_LEVELS = [
  { value: 'unacceptable', label: 'Unacceptable', color: 'red', description: 'Prohibited uses (social scoring, subliminal manipulation)' },
  { value: 'high', label: 'High Risk', color: 'orange', description: 'Critical applications (employment, law enforcement, healthcare)' },
  { value: 'limited', label: 'Limited Risk', color: 'blue', description: 'Transparency obligations (chatbots, content recommendation)' },
  { value: 'minimal', label: 'Minimal Risk', color: 'green', description: 'No specific obligations beyond general principles' }
];

function CTRL_02_ConfigPanel({ projectId, controlCode, project }) {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [config, setConfig] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [activeKeys, setActiveKeys] = useState(['1']);

  useEffect(() => {
    if (projectId && controlCode) {
      fetchConfig();
    }
  }, [projectId, controlCode]);

  const fetchConfig = async () => {
    setLoading(true);
    try {
      const response = await getControlConfig(projectId, controlCode);
      setConfig(response.data);
    } catch (error) {
      console.error('Failed to fetch config:', error);
      setConfig({
        enabled: false,
        config: {
          risk_levels_required: ['unacceptable', 'high', 'limited', 'minimal'],
          high_risk_requires_oversight: true,
          auto_classify: false,
          classification_criteria: {}
        }
      });
      if (error.response && error.response.status !== 404) {
        showError('Failed to load configuration. Using defaults.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await saveControlConfig(projectId, controlCode, config);
      showSuccess('Configuration saved successfully');
      setHasChanges(false);
      fetchConfig();
    } catch (error) {
      console.error('Failed to save config:', error);
      showError(error);
    } finally {
      setSaving(false);
    }
  };

  const updateConfig = (field, value) => {
    setConfig({
      ...config,
      config: {
        ...config.config,
        [field]: value
      }
    });
    setHasChanges(true);
  };

  if (loading) {
    return (
      <Card>
        <div style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}>
          <Spin size="large" />
        </div>
      </Card>
    );
  }

  if (!config) return null;

  const configData = config.config || {};
  const isConfigured = config.enabled && configData.risk_levels_required?.length > 0;
  const statusIcon = isConfigured ? <CheckCircleOutlined style={{ color: '#52c41a' }} /> : <WarningOutlined style={{ color: '#faad14' }} />;
  const statusText = isConfigured ? 'Configured' : 'Setup Required';

  return (
    <Card
      title={
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <SettingOutlined />
          <span>CTRL_02 Configuration: Model Risk Classification</span>
          {statusIcon}
          <Tag color={isConfigured ? 'green' : 'orange'}>{statusText}</Tag>
        </div>
      }
      extra={
        <Button
          type="primary"
          icon={<SaveOutlined />}
          onClick={handleSave}
          loading={saving}
          disabled={!hasChanges}
        >
          Save Changes
        </Button>
      }
    >
      <Collapse activeKey={activeKeys} onChange={setActiveKeys} expandIconPosition="right">
        <Panel
          header={
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <strong>1. Enable Risk Classification</strong>
              {config.enabled && <Tag color="green">Active</Tag>}
            </div>
          }
          key="1"
        >
          <div style={{ padding: '16px 0' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '16px' }}>
              <Switch
                checked={config.enabled}
                onChange={(checked) => setConfig({ ...config, enabled: checked })}
                size="large"
              />
              <div>
                <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>
                  {config.enabled ? 'Risk Classification Enabled' : 'Risk Classification Disabled'}
                </div>
                <div style={{ color: '#888', fontSize: '13px' }}>
                  {config.enabled
                    ? 'Enforcing EU AI Act risk classification for all models'
                    : 'Enable to classify models by risk level and ensure compliance'}
                </div>
              </div>
            </div>

            {!config.enabled && (
              <Alert
                type="info"
                message="About Model Risk Classification"
                description="This control enforces EU AI Act risk classification (unacceptable, high, limited, minimal) for all AI models. High-risk models require additional oversight and documentation."
                showIcon
                style={{ marginTop: '16px' }}
              />
            )}
          </div>
        </Panel>

        <Panel
          header={
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <strong>2. Risk Classification Rules</strong>
              {configData.risk_levels_required?.length > 0 && <Tag color="blue">{configData.risk_levels_required.length} levels</Tag>}
            </div>
          }
          key="2"
          disabled={!config.enabled}
        >
          <div style={{ padding: '16px 0' }}>
            <div style={{ marginBottom: '24px' }}>
              <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '12px' }}>
                Required Risk Levels
              </label>
              <div style={{ display: 'grid', gap: '12px' }}>
                {RISK_LEVELS.map(level => (
                  <div key={level.value} style={{ border: '1px solid #d9d9d9', borderRadius: '4px', padding: '12px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <Checkbox
                        checked={configData.risk_levels_required?.includes(level.value)}
                        onChange={(e) => {
                          const current = configData.risk_levels_required || [];
                          const updated = e.target.checked
                            ? [...current, level.value]
                            : current.filter(v => v !== level.value);
                          updateConfig('risk_levels_required', updated);
                        }}
                      />
                      <Tag color={level.color}>{level.label}</Tag>
                      <div style={{ fontSize: '13px', color: '#666' }}>{level.description}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div style={{ marginBottom: '24px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <Switch
                  checked={configData.high_risk_requires_oversight ?? true}
                  onChange={(checked) => updateConfig('high_risk_requires_oversight', checked)}
                />
                <label style={{ fontWeight: 'bold' }}>High-Risk Models Require Human Oversight</label>
              </div>
              <div style={{ fontSize: '12px', color: '#888', marginTop: '4px', marginLeft: '44px' }}>
                Enforce that all high-risk and unacceptable-risk models have human oversight configured
              </div>
            </div>

            <div style={{ marginBottom: '24px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <Switch
                  checked={configData.auto_classify ?? false}
                  onChange={(checked) => updateConfig('auto_classify', checked)}
                />
                <label style={{ fontWeight: 'bold' }}>Auto-Classify Based on Use Case</label>
              </div>
              <div style={{ fontSize: '12px', color: '#888', marginTop: '4px', marginLeft: '44px' }}>
                Automatically classify models based on use case keywords and impact assessment
              </div>
            </div>

            <Alert
              type="success"
              message="Configuration Summary"
              description={
                <div>
                  <p><strong>Risk Levels Required:</strong> {configData.risk_levels_required?.length || 0} levels configured</p>
                  <p><strong>Oversight Required:</strong> {configData.high_risk_requires_oversight ? 'Yes' : 'No'}</p>
                  <p><strong>Auto-Classification:</strong> {configData.auto_classify ? 'Enabled' : 'Disabled'}</p>
                </div>
              }
              showIcon
              style={{ marginTop: '16px' }}
            />
          </div>
        </Panel>
      </Collapse>
    </Card>
  );
}

export default CTRL_02_ConfigPanel;
